import FileSaver from "file-saver";
import ObjectId from "bson-objectid";

import api from "@/api";

import deepClone from "@/utils/deep-clone";

function generateDefaultGoal(user) {
    return {
        _id: ObjectId().toString(),
        parent: null,
        type: "goalUser",
        localTeam: null,
        user: { _id: user._id },
        visibility:
            user.rights.G_PUBLIC_BY_DEFAULT && !user.rights.G_DEACTIVATE_PUBLIC ? "all" : "limited",
        visibleTo: [],
        managed: Boolean(user.teamRole.reportsTo && !user.rights.G_MANAGED_DISABLED),
        managedBy: {
            _id:
                user.teamRole.reportsTo && !user.rights.G_MANAGED_DISABLED
                    ? user.teamRole.reportsTo
                    : user._id,
        },
        access: {
            canSee: true,
            canUpdateGoal: true,
            canUpdateStatus: true,
            canDelete: false,
        },
        progress: [],
        children: [],
        status: "draft",
        dynamicProgressEnabled: false,
        name: "",
        description: "",
        icon: "/img/icons/puzzle.png",
        metricType: "percentage",
        metricCurrent: "0",
        metricEnd: "100",
        metricWeight: "",
        cycle: null,
        keyResults: [],
        tags: [],
        titleOwnership: "Draft",
        isNew: true,
    };
}

const state = {
    list: [],
    stats: {},
    statsList: [],
    statsGlobal: {},
    statsChartData: [],
    statsChartLabels: [],
    filters: {},
    feed: [],

    details: {},
    progress: [],
};

const getters = {
    list(state) {
        return deepClone(state?.list) ?? [];
    },
    stats(state) {
        return state?.stats ?? {};
    },
    statsList(state) {
        return state?.statsList ?? [];
    },
    statsGlobal(state) {
        return state?.statsGlobal ?? {};
    },
    statsChartData(state) {
        return state?.statsChartData ?? [];
    },
    statsChartLabels(state) {
        return state?.statsChartLabels ?? [];
    },
    filters(state) {
        return state?.filters ?? {};
    },
    feed(state) {
        return state?.feed ?? [];
    },

    details(state) {
        return id => deepClone(state.details[id] ?? {});
    },
    progress(state) {
        return state?.progress || [];
    },
};

const actions = {
    getList({ commit }, { payload: { listSegment, listCustomFilter, output } }) {
        return api.post(`/api/goals/list`, { listSegment, listCustomFilter, output }).then(res => {
            switch (output) {
                case "profile":
                case "list":
                case "treePartial":
                case "treeFull": {
                    const list = res?.data?.goals ?? [];
                    const stats = res?.data?.stats ?? {};
                    const filters = res?.data?.filters ?? {};

                    commit("updateList", { list });
                    commit("updateStats", { stats });
                    commit("updateFilters", { filters });
                    break;
                }
                case "stats": {
                    const statsList = res?.data?.stats?.list ?? [];
                    const statsGlobal = res?.data?.stats?.global?.length
                        ? res?.data?.stats?.global[0]
                        : {};
                    const statsChartData = res?.data?.stats?.chartData ?? [];
                    const statsChartLabels = res?.data?.stats?.chartLabels ?? [];
                    const filters = res?.data?.filters ?? {};

                    commit("updateStatsList", { statsList: statsList });
                    commit("updateStatsGlobal", { statsGlobal });
                    commit("updateStatsChartData", { statsChartData });
                    commit("updateStatsChartLabels", { statsChartLabels });
                    commit("updateFilters", { filters });
                    break;
                }
                case "feed": {
                    const feed = res?.data?.feed ?? [];
                    const filters = res?.data?.filters ?? {};

                    commit("updateFeed", { feed });
                    commit("updateFilters", { filters });
                    break;
                }
                default: {
                    const list = res?.data?.goals ?? [];
                    const filters = res?.data?.filters ?? {};

                    commit("updateList", { list });
                    commit("updateFilters", { filters });
                    break;
                }
            }
        });
    },

    getDetails({ commit, state }, { payload: { id } }) {
        return api
            .get(`/api/goals/${id}`)
            .then(res => {
                let goal = res.data;

                // Parse deadline
                goal.deadline = goal.deadline ? new Date(goal.deadline) : "";
                goal.keyResults = goal.keyResults.map(keyResult => ({
                    ...keyResult,
                    deadline: keyResult.deadline ? new Date(keyResult.deadline) : "",
                }));

                commit("updateDetails", { goal });
            })
            .catch(err => {
                if (
                    err?.response?.status === 404 &&
                    Object.keys(getters.details(state)(id)).length
                ) {
                    return;
                }

                throw err;
            });
    },

    create({ commit, rootGetters }, { payload: { goal = {} } = {} }) {
        const user = rootGetters["users/current/user"];

        if (!user) {
            throw new Error();
        }

        // Generate new goal based on defaults
        const newGoal = {
            ...generateDefaultGoal(user),
            ...goal,
        };

        commit("updateDetails", { goal: newGoal });
    },

    update({ commit, state }, { payload: { goal } }) {
        return api.post(`/api/goals`, goal).then(res => {
            const existingGoal = getters.details(state)(goal._id);
            const updatedGoal = res.data;

            if (updatedGoal) {
                commit("updateDetails", {
                    goal: {
                        ...existingGoal,
                        ...updatedGoal,
                    },
                });
            }
        });
    },

    bulkUpdate({ commit }, { payload: { goals, scope } }) {
        return api.put(`/api/goals`, { goals, scope }).then(() => {
            commit("updateList", { list: goals });
        });
    },

    export(_, { payload: { listSegment, listCustomFilter, output = "export" } }) {
        return api
            .post(
                `/api/goals/list`,
                { listSegment, listCustomFilter, output },
                { responseType: "arraybuffer" },
            )
            .then(res => {
                const date = new Date();
                const filename = "Goals-Export-" + date.toLocaleDateString("de") + ".xlsx";

                FileSaver.saveAs(
                    new Blob([res.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }),
                    filename,
                );
            });
    },

    getProgress({ commit }, { payload: { start, end, listCustomFilter, listSegment, output } }) {
        return api
            .post(`/api/goals/updates?from=${start}&to=${end}`, {
                listCustomFilter,
                listSegment,
                output,
            })
            .then(res => {
                commit("updateProgress", { progress: res.data });
            });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateStats(state, { stats }) {
        state.stats = { ...stats };
    },
    updateStatsList(state, { statsList }) {
        state.statsList = [...statsList];
    },
    updateStatsGlobal(state, { statsGlobal }) {
        state.statsGlobal = { ...statsGlobal };
    },
    updateStatsChartData(state, { statsChartData }) {
        state.statsChartData = [...statsChartData];
    },
    updateStatsChartLabels(state, { statsChartLabels }) {
        state.statsChartLabels = [...statsChartLabels];
    },
    updateFilters(state, { filters }) {
        state.filters = { ...filters };
    },
    updateFeed(state, { feed }) {
        state.feed = [...feed];
    },

    updateDetails(state, { goal }) {
        state.details[goal._id] = {
            ...goal,
        };
    },
    updateProgress(state, { progress }) {
        state.progress = progress;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
